/**
 * CR164: Full OCR Viewer
 * CR465: Added Preview/Full toggle + character count indicator + quality hint
 * Enhanced OCR text viewer with full text, search, copy, download, and tiled view
 */

import { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { 
  Search, Copy, Download, FileText, Grid3X3, 
  CheckCircle, X, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import type { TileOcrResult } from '@/lib/flyer-pipeline-v2/dealLocatorTypes';

interface FullOcrViewerProps {
  /** Preview text (first 500 chars) */
  previewText?: string;
  /** Full OCR text */
  fullText?: string;
  /** Whether full text is still loading */
  isLoading?: boolean;
  /** Tile results if tiled scanning was used */
  tileResults?: TileOcrResult[];
  /** Page number for file naming */
  pageNumber?: number;
  /** Merchant name for file naming */
  merchantName?: string;
}

/** CR465: Quality hint based on character count */
function getOcrQualityHint(charCount: number): { label: string; color: string } {
  if (charCount < 200) return { label: "Low", color: "text-amber-600 dark:text-amber-400" };
  if (charCount <= 1000) return { label: "Medium", color: "text-blue-600 dark:text-blue-400" };
  return { label: "High", color: "text-green-600 dark:text-green-400" };
}

export function FullOcrViewer({
  previewText,
  fullText,
  isLoading = false,
  tileResults,
  pageNumber = 1,
  merchantName = 'page',
}: FullOcrViewerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'full' | 'tiles'>('full');
  const [expandedTiles, setExpandedTiles] = useState<Set<number>>(new Set());
  // CR465: Preview/Full text mode toggle
  const [textMode, setTextMode] = useState<'preview' | 'full'>('preview');

  const hasFullText = Boolean(fullText);
  const hasTiles = Boolean(tileResults && tileResults.length > 0);

  // CR465: Determine which text to show based on toggle
  const displayText = useMemo(() => {
    if (textMode === 'full' && fullText) return fullText;
    if (textMode === 'full' && !fullText) return previewText || ''; // fallback
    return previewText || fullText || '';
  }, [textMode, fullText, previewText]);

  // CR465: Character count
  const charCount = displayText.length;
  const fullCharCount = (fullText || previewText || '').length;
  const qualityHint = getOcrQualityHint(fullCharCount);

  // Search and highlight matching text
  const { highlightedText, matchCount } = useMemo(() => {
    if (!searchQuery.trim() || !displayText) {
      return { highlightedText: displayText, matchCount: 0 };
    }

    const query = searchQuery.toLowerCase();
    const text = displayText;
    let count = 0;
    let lastIndex = 0;
    const parts: Array<{ text: string; isMatch: boolean }> = [];

    let searchIndex = text.toLowerCase().indexOf(query);
    while (searchIndex !== -1) {
      if (searchIndex > lastIndex) {
        parts.push({ text: text.substring(lastIndex, searchIndex), isMatch: false });
      }
      parts.push({ text: text.substring(searchIndex, searchIndex + query.length), isMatch: true });
      count++;
      lastIndex = searchIndex + query.length;
      searchIndex = text.toLowerCase().indexOf(query, lastIndex);
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.substring(lastIndex), isMatch: false });
    }

    return { highlightedText: parts, matchCount: count };
  }, [displayText, searchQuery]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(displayText);
      toast.success('OCR text copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy text');
    }
  }, [displayText]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([displayText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${merchantName}_page${pageNumber}_ocr.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('OCR text downloaded');
  }, [displayText, merchantName, pageNumber]);

  const toggleTile = (index: number) => {
    setExpandedTiles(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const renderHighlightedText = () => {
    if (typeof highlightedText === 'string') {
      return <pre className="text-xs whitespace-pre-wrap font-mono">{highlightedText}</pre>;
    }

    return (
      <pre className="text-xs whitespace-pre-wrap font-mono">
        {highlightedText.map((part, i) => (
          part.isMatch ? (
            <mark key={i} className="bg-yellow-300 dark:bg-yellow-700 px-0.5 rounded">
              {part.text}
            </mark>
          ) : (
            <span key={i}>{part.text}</span>
          )
        ))}
      </pre>
    );
  };

  return (
    <div className="space-y-3">
      {/* Header with view mode tabs and actions */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {hasTiles ? (
            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'full' | 'tiles')}>
              <TabsList className="h-8">
                <TabsTrigger value="full" className="text-xs h-7 px-3">
                  <FileText className="h-3 w-3 mr-1" />
                  Full-page OCR
                </TabsTrigger>
                <TabsTrigger value="tiles" className="text-xs h-7 px-3">
                  <Grid3X3 className="h-3 w-3 mr-1" />
                  Per-tile ({tileResults?.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          ) : (
            <div className="flex items-center gap-2">
              {/* CR465: Preview/Full toggle */}
              {hasFullText && previewText && fullText !== previewText ? (
                <Tabs value={textMode} onValueChange={(v) => setTextMode(v as 'preview' | 'full')}>
                  <TabsList className="h-7">
                    <TabsTrigger value="preview" className="text-xs h-6 px-2">
                      Preview
                    </TabsTrigger>
                    <TabsTrigger value="full" className="text-xs h-6 px-2">
                      Full
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              ) : (
                <Badge variant={hasFullText ? "default" : "outline"} className="text-xs">
                  {hasFullText ? "Full text" : `Preview (${previewText?.length || 0} chars)`}
                </Badge>
              )}
              {isLoading && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* CR465: Character count + quality hint */}
          <div className="flex items-center gap-1.5 mr-2 text-xs">
            <span className="text-muted-foreground">{charCount.toLocaleString()} chars</span>
            <span className={`font-medium ${qualityHint.color}`}>({qualityHint.label})</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            disabled={!displayText}
            className="h-7 text-xs"
          >
            <Copy className="h-3 w-3 mr-1" />
            Copy
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            disabled={!displayText}
            className="h-7 text-xs"
          >
            <Download className="h-3 w-3 mr-1" />
            .txt
          </Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search within OCR text..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8 h-8 text-xs"
        />
        {searchQuery && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <Badge variant="secondary" className="text-xs">
              {matchCount} match{matchCount !== 1 ? 'es' : ''}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchQuery('')}
              className="h-5 w-5 p-0"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      {/* Content area */}
      {viewMode === 'full' ? (
        /* Full-page OCR view */
        displayText ? (
          <ScrollArea className="h-72 border rounded-lg p-3 bg-muted/30">
            {renderHighlightedText()}
          </ScrollArea>
        ) : (
          <div className="h-32 border rounded-lg flex items-center justify-center text-muted-foreground text-sm">
            No OCR text available
          </div>
        )
      ) : (
        /* Per-tile view */
        <ScrollArea className="h-72 border rounded-lg">
          <div className="p-2 space-y-2">
            {tileResults?.map((tile, i) => (
              <div key={i} className="border rounded bg-background">
                <button
                  className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
                  onClick={() => toggleTile(i)}
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs font-mono">
                      Tile {tile.index + 1}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      ({tile.coords.x}, {tile.coords.y}) — {tile.text.length} chars
                    </span>
                    {tile.matchedKeywords.length > 0 && (
                      <Badge className="text-xs bg-green-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {tile.matchedKeywords.length} match
                      </Badge>
                    )}
                  </div>
                  {expandedTiles.has(i) ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>

                {expandedTiles.has(i) && (
                  <div className="px-3 pb-3 border-t bg-muted/20">
                    {/* Tile metadata */}
                    <div className="flex gap-2 py-2 text-xs text-muted-foreground">
                      <span>Coords: ({tile.coords.x}, {tile.coords.y})</span>
                      <span>Size: {tile.coords.width}×{tile.coords.height}</span>
                      <span>Confidence: {tile.confidence.toFixed(0)}%</span>
                    </div>
                    
                    {/* Matched keywords */}
                    {tile.matchedKeywords.length > 0 && (
                      <div className="flex flex-wrap gap-1 py-2">
                        {tile.matchedKeywords.map((kw, j) => (
                          <Badge key={j} variant="secondary" className="text-xs">
                            {kw}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Tile OCR text */}
                    <ScrollArea className="h-32 border rounded p-2 bg-background">
                      <pre className="text-xs whitespace-pre-wrap font-mono">
                        {tile.text || '(empty)'}
                      </pre>
                    </ScrollArea>
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
